import { useDndMonitor, useDroppable, type DragMoveEvent } from "@dnd-kit/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import KanbanColumnContentView from "./KanbanColumnContentView";
import type { PlayerDropData } from "@/features/player-kanban/types/kanbanColumnContentTypes";
import type { Player } from "@/features/player-kanban/types/types";
import { getInitialPlayerTags } from "@/features/player-kanban/utils/getPlayerTags";
import { resolveColumnInsertionIndex } from "@/features/player-kanban/utils/columnInsertion";
import { useLazyColumnCards } from "@/features/player-kanban/hooks/useLazyColumnCards";

/** This lane owns no gap at all — the state between drags. */
const NO_GAP = { index: -1, isOpen: false } as const;

/** The live drop gap this column is holding for the dragged card. */
interface DropGapState {
  /**
   * Slot in the rendered player list. -1 means the lane owns no gap element;
   * any other value means it owns one AT that slot, open or not.
   */
  readonly index: number;
  /**
   * Whether the gap is currently held OPEN. Separate from `index` so the gap
   * can animate shut in the slot it opened at, instead of vanishing.
   */
  readonly isOpen: boolean;
}

/**
 * Props for the KanbanColumnContentContainer component
 */
interface KanbanColumnContentContainerProps {
  /**
   * Unique identifier for the column used for drop zone identification
   */
  columnId: string;
  /**
   * Array of player objects to display in the column
   */
  players: Player[];
  /**
   * Indicates whether the column has no players to display
   */
  isEmpty: boolean;
  /**
   * Message text to display when the column is empty
   */
  emptyText: string;
  /**
   * Indicates if a drag operation is active anywhere on the board
   */
  isGloballyDragging?: boolean;
  /**
   * Indicates if a player is currently being dragged from this specific column
   */
  isDraggingFromThisColumn: boolean;
  /**
   * Unique identifier of the kanban board this column belongs to
   */
  boardId?: string;
  /**
   * Trigger value that increments to force re-rendering of board state
   */
  boardRefreshTrigger?: number;
  /**
   * Indicates whether the current user has permission to edit the board
   */
  canEdit?: boolean;
  /**
   * SE-4685: Whether this column is sorted by Rank and editable, enabling the
   * within-column drag-to-reorder grab handle on each card.
   */
  isRankReorderEnabled?: boolean;
  /**
   * Callback invoked when a player card is clicked to view details
   */
  onPlayerClick: (playerId: string) => void;
  /**
   * Optional callback invoked when a player is dropped into this column
   */
  onPlayerDrop?: (playerData: PlayerDropData) => void;
  /**
   * Callback invoked when a drag operation starts within this column
   */
  onLocalDragStart: () => void;
  /**
   * Callback invoked when a drag operation ends within this column
   */
  onLocalDragEnd: () => void;
  /**
   * Optional callback to trigger horizontal scrolling based on drag position
   */
  onAutoScroll?: (dragX: number) => void;
  /**
   * Internal callback for handling tag additions to players (prefixed with underscore for internal use)
   */
  _onTagAdded?: (playerId: string, tag: string) => void;
  /**
   * Internal function to retrieve tags associated with a specific player (prefixed with underscore for internal use)
   */
  _getPlayerTags?: (playerId: string) => string[];
  /**
   * Optional callback to manually refresh the board data
   */
  onBoardRefresh?: () => void;
  /**
   * SE-6554 (Figma 80:16392): reports this lane's `isOver` to the column, which
   * draws the drag-over outline around itself — header included, which is why
   * the flag cannot simply be used where it is measured.
   */
  onDragOverChange?: (isOver: boolean) => void;
}

/**
 * Container component that manages kanban column content logic including drag-and-drop behavior, player interactions, and visual feedback.
 * @param root0 - The component props
 * @param root0.columnId - Unique identifier for the column used for drop zone identification
 * @param root0.players - Array of player objects to display in the column
 * @param root0.isEmpty - Indicates whether the column has no players to display
 * @param root0.emptyText - Message text to display when the column is empty
 * @param root0.isGloballyDragging - Indicates if a drag operation is active anywhere on the board
 * @param root0.isDraggingFromThisColumn - Indicates if a player is currently being dragged from this specific column
 * @param root0.boardId - Unique identifier of the kanban board this column belongs to
 * @param root0.boardRefreshTrigger - Trigger value that increments to force re-rendering of board state
 * @param root0.onPlayerClick - Callback invoked when a player card is clicked to view details
 * @param root0.onPlayerDrop - Optional callback invoked when a player is dropped into this column
 * @param root0.onLocalDragStart - Callback invoked when a drag operation starts within this column
 * @param root0.onLocalDragEnd - Callback invoked when a drag operation ends within this column
 * @param root0.onAutoScroll - Optional callback to trigger horizontal scrolling based on drag position
 * @param root0._onTagAdded - Internal callback for handling tag additions to players (prefixed with underscore for internal use)
 * @param root0._getPlayerTags - Internal function to retrieve tags associated with a specific player (prefixed with underscore for internal use)
 * @param root0.onBoardRefresh - Optional callback to manually refresh the board data
 * @param root0.onDragOverChange - SE-6554: reports this lane's `isOver` up to the column that outlines itself with it
 * @param root0.canEdit - Indicates whether the current user has permission to edit the board
 * @param root0.isRankReorderEnabled - SE-4685: whether Rank-sort within-column drag-reorder is active for this column
 */
const KanbanColumnContentContainer = ({
  columnId,
  players,
  isEmpty,
  emptyText,
  isGloballyDragging = false,
  isDraggingFromThisColumn,
  boardId,
  boardRefreshTrigger,
  canEdit,
  isRankReorderEnabled,
  onPlayerClick,
  onPlayerDrop,
  onLocalDragStart,
  onLocalDragEnd,
  onAutoScroll,
  _onTagAdded,
  _getPlayerTags,
  onBoardRefresh,
  onDragOverChange,
}: KanbanColumnContentContainerProps) => {
  // Set up droppable with @dnd-kit
  const droppableId = `column-${columnId}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: {
      type: "column",
      columnId,
    },
  });

  // Combine style classes based on drag state.
  //
  // SE-6554 item N: the border WIDTH is constant at 2px in all three states —
  // transparent at rest. It used to go 0 → 1 → 2 across rest/dragging/over,
  // and because RN-Web lays borders out box-sizing: border-box, every lane the
  // card crossed reflowed its own contents by a pixel or two. That jitter, on
  // every lane boundary, is a real part of what read as an unsteady drag.
  // Holding the width fixed and animating only the colours keeps the column
  // interior perfectly still while the chrome fades in and out.
  //
  // SE-6554 (Figma 80:16392) — WHAT THIS NO LONGER DRAWS, and why.
  //
  // The drag-over state used to light this box up with a solid accent border
  // and an accent wash. The design says the opposite on both counts: the
  // drag-over affordance is a DASHED outline, it belongs around the whole
  // column (stage name included, so it cannot be drawn from here — see
  // KanbanColumnView), and "Column bg unchanged". The aimed lane therefore
  // falls back to `base` here and gets its outline from the column.
  //
  // The faint dashed hint every OTHER lane used to draw during a drag is gone
  // (ratified by Raniel): it had no cell in the design node, and lighting up
  // every lane at once says "all of these are droppable" — which the board
  // already implies — while competing with the one signal that matters, the
  // aimed lane's own outline. Exactly one lane wears chrome during a drag.
  const dropZoneClassName = useMemo(() => {
    /* 12px inset from the column edges, per the design's content frame
       (x=12 in a 264px column), leaving the full remaining width to cards. */
    const base =
      "web:transition-all web:duration-150 m-3 flex flex-1 flex-col overflow-visible rounded-sm border-2 border-solid border-transparent";
    return base;
  }, []);
  const createPlayerClickHandler = useCallback(
    (playerId: string) => () => {
      console.log(
        "🎪 [COLUMN CONTENT] Player click handler called for:",
        playerId
      );
      onPlayerClick(playerId);
    },
    [onPlayerClick]
  );

  const handleDrop = useCallback(
    (data: PlayerDropData) => {
      console.log(
        `🎯 KANBAN DROP: Player "${data.playerName}" dropped in column`
      );
      if (onPlayerDrop) {
        onPlayerDrop(data);
      }
    },
    [onPlayerDrop]
  );

  const handleTagAdded = useCallback((playerId: string, tag: string) => {
    console.log(`🏷️  KANBAN TAG: Added "${tag}" to player ${playerId}`);
    // TODO: Add tag persistence logic here
  }, []);

  /*
   * SE-6554 item N — THE LIVE DROP GAP.
   *
   * As the dragged card crosses into this lane, the lane opens a gap of exactly
   * the card's height at exactly the slot the card would land in, and the cards
   * below slide down to make room. Leaving the lane closes it again.
   *
   * ## Why a monitor and not the drag context
   *
   * The previous implementation read `over`/`active` off `useDndContext` in a
   * `useMemo`. `over` only changes when the drop TARGET changes, and `active` is
   * a stable object whose rect lives behind a mutable ref — so the memo did not
   * re-run as the pointer moved within a column, and the indicator could not
   * track it even if the arithmetic had been right (it was not — see
   * `resolveColumnInsertionIndex`). `onDragMove` fires on every sensor move,
   * which is the only signal with the resolution this needs.
   *
   * ## Why no mutation, and no cross-container array move
   *
   * The gap is a rendered PLACEHOLDER, not a relocation of the card. Nothing
   * moves between columns until drop: no Apollo write fires mid-drag, the board
   * arrays are never rewritten, and `pendingMoves` is untouched. A card
   * therefore cannot be duplicated (it is never inserted anywhere) nor lost (it
   * is never removed from anywhere), and drag CANCEL needs no reconciliation —
   * dropping the gap state restores the exact prior render.
   *
   * The dnd-kit multiple-sortable-containers pattern would move the card between
   * arrays here instead. It is the wrong instrument for this board: outside a
   * Rank-sorted column the cards are not sortables (they are plain rows with a
   * card-level `useDraggable`), so an array move would land as an instantaneous
   * reflow — the cards below would teleport rather than slide — while adding the
   * one failure mode that must not happen here. The placeholder gives the same
   * user-visible promise with a CSS height transition doing the sliding.
   */
  const [dropGap, setDropGap] = useState<DropGapState>(NO_GAP);

  // Leaving the lane: close the gap IN PLACE — the slot is preserved so it
  // animates shut where it opened. Zeroing the slot at the same time would jump
  // the still-open gap to the end of the lane on its way out.
  const collapseGap = useCallback(
    () => setDropGap(prev => (prev.isOpen ? { ...prev, isOpen: false } : prev)),
    []
  );

  // Drag over: drop the gap entirely. On a drop the real card arrives in the
  // same slot; on Esc the lane simply returns to its pre-drag render, with no
  // residual state left anywhere to reconcile.
  const resetGap = useCallback(
    () => setDropGap(prev => (prev.index === -1 ? prev : NO_GAP)),
    []
  );

  const trackGap = useCallback(
    ({
      active,
      over,
    }: {
      active: DragMoveEvent["active"];
      over: DragMoveEvent["over"];
    }) => {
      const draggedData = active.data.current;
      const isHoldingGap =
        over?.id === droppableId &&
        // A rank reorder already opens its own gap through the sortable
        // transform inside its column — a second, placeholder gap would double
        // the space the card is promised.
        draggedData?.dragType !== "rank-reorder" &&
        // The SOURCE lane keeps the (hidden) card's own footprint, so it is
        // already holding the card's space. Opening a gap there too would push
        // the lane open by two card heights for one card.
        !players.some(player => player.id === draggedData?.id);

      if (!isHoldingGap) {
        collapseGap();
        return;
      }

      const index = resolveColumnInsertionIndex(
        { id: columnId, players },
        active.rect.current.translated
      );
      setDropGap(prev =>
        prev.index === index && prev.isOpen ? prev : { index, isOpen: true }
      );
    },
    [collapseGap, columnId, droppableId, players]
  );

  const dndMonitorListeners = useMemo(
    () => ({
      onDragMove: trackGap,
      onDragOver: trackGap,
      onDragEnd: resetGap,
      onDragCancel: resetGap,
    }),
    [trackGap, resetGap]
  );

  // Prepare player data with initial tags
  const playersWithTags = useMemo(
    () =>
      players.map(player => ({
        ...player,
        initialTags: getInitialPlayerTags(player.id),
      })),
    [players]
  );

  // SE-5492: long columns mount their cards progressively instead of all at
  // once. Growth-only — no card is ever unmounted — so a card cannot vanish
  // from @dnd-kit's registry mid-drag. Growth pauses while a drag is active.
  const { visibleCount, hasMore, sentinelRef } = useLazyColumnCards({
    total: playersWithTags.length,
    isDragging: isGloballyDragging,
  });

  const visiblePlayers = useMemo(
    () => playersWithTags.slice(0, visibleCount),
    [playersWithTags, visibleCount]
  );

  // Subscribes this lane to the board's drag events; see the drop-gap note
  // above. Registered after every definition it depends on, per the
  // definitions → side effects → return ordering rule.
  useDndMonitor(dndMonitorListeners);

  // SE-6554 (Figma 80:16392): hand this lane's `isOver` to the column so it can
  // outline itself around the stage name too. Only ever fires on the flip —
  // entering or leaving — so a drag that stays inside one lane costs nothing,
  // and a drag that ends anywhere clears it, because dnd-kit drops `isOver`
  // itself when the drag is over.
  useEffect(() => {
    onDragOverChange?.(isOver);
  }, [isOver, onDragOverChange]);

  return (
    <KanbanColumnContentView
      columnId={columnId}
      players={visiblePlayers}
      totalPlayers={playersWithTags.length}
      hasMoreCards={hasMore}
      sentinelRef={sentinelRef}
      isEmpty={isEmpty}
      emptyText={emptyText}
      isGloballyDragging={isGloballyDragging}
      isDraggingFromThisColumn={isDraggingFromThisColumn}
      isOver={isOver}
      boardId={boardId}
      boardRefreshTrigger={boardRefreshTrigger}
      insertionIndex={dropGap.index}
      isDropGapOpen={dropGap.isOpen}
      dropZoneClassName={dropZoneClassName}
      canEdit={canEdit}
      isRankReorderEnabled={isRankReorderEnabled}
      setNodeRef={setNodeRef}
      createPlayerClickHandler={createPlayerClickHandler}
      handleDrop={handleDrop}
      handleTagAdded={handleTagAdded}
      onLocalDragStart={onLocalDragStart}
      onLocalDragEnd={onLocalDragEnd}
      onAutoScroll={onAutoScroll}
      onBoardRefresh={onBoardRefresh}
    />
  );
};

export default React.memo(KanbanColumnContentContainer);
